import React, { createContext, useState } from 'react';
import {invalidateQueries, queryKeys} from '../Components/Hooks/useQueryHooks';

/**
 * Context for managing the refresh state of sign document lists.
 * Provides a trigger value and a function to refresh the list.
 *
 * Now also invalidates React Query cache for SignDocs to ensure
 * components using useAllAdminSignDocsQuery are also refreshed.
 *
 * @typedef {Object} SignDocListRefresherContextType
 * @property {number} refreshTrigger - A counter that triggers a refresh when incremented.
 * @property {() => void} refreshSignDocList - Function to increment the refreshTrigger and invalidate React Query cache.
 */

/**
 * Context for SignDocListRefresher.
 * @type {React.Context<SignDocListRefresherContextType>}
 */
export const SignDocListRefresherContext = createContext({
  refreshTrigger: 0,
  refreshSignDocList: () => {},
});

/**
 * Provider component for SignDocListRefresherContext.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be rendered within the provider
 * @returns {JSX.Element} The provider component wrapping its children
 */
export const SignDocListRefresherProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /**
   * Function to refresh the sign document list by:
   * 1. Incrementing the refreshTrigger (for legacy components)
   * 2. Invalidating React Query cache (for components using useAllAdminSignDocsQuery)
   */
  const refreshSignDocList = () => {
    // Legacy: increment trigger for components that listen to refreshTrigger
    setRefreshTrigger(prev => prev + 1);

    // React Query: invalidate SignDocs cache for all parameter combinations
    invalidateQueries({queryKeys: [
      queryKeys.allAdminSignDocs(false),
      queryKeys.allAdminSignDocs(true),
      queryKeys.uniqueSignDocs(false),
      queryKeys.uniqueSignDocs(true),
      queryKeys.adminCreatedSignDocTypes(false),
      queryKeys.adminCreatedSignDocTypes(true)
    ]});
  };

  return (
    <SignDocListRefresherContext.Provider value={{ refreshTrigger, refreshSignDocList }}>
      {children}
    </SignDocListRefresherContext.Provider>
  );
};